import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../common/DataTable";
import { useAuth } from "../../../context/AuthContext";
import {
  createTPRMDomainScanApi,
  getTPRMCustomerAdminOptionsApi,
  getTPRMDomainFindingsApi,
  getTPRMDomainScanApi,
  getTPRMDomainScansApi,
  getTPRMVendorServicesApi,
  getTPRMVendorsApi,
  promoteTPRMDomainFindingApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import {
  displayTPRMValue,
  formatTPRMTimestamp,
  getTPRMErrorMessage,
  normalizeTPRMPage,
} from "./tprmPageUtils";

const PAGE_SIZE = 10;

const STATUS_STYLE = {
  Completed: "text-emerald-700 dark:text-emerald-300",
  Partial: "text-amber-700 dark:text-amber-300",
  Failed: "text-rose-700 dark:text-rose-300",
  Blocked: "text-rose-700 dark:text-rose-300",
  Running: "text-blue-700 dark:text-blue-300",
};

const DomainPosturePanel = () => {
  const navigate = useNavigate();
  const { user, isPlatformAdmin, isCustomerAdmin } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const platformAdmin = Boolean(isPlatformAdmin) ||
    ["admin", "platform_admin", "superadmin"].includes(role);
  const canStartScan = platformAdmin || Boolean(isCustomerAdmin) || role === "customer_admin";
  const [tenantOptions, setTenantOptions] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [tenantError, setTenantError] = useState("");
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vendorError, setVendorError] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [services, setServices] = useState([]);
  const [serviceError, setServiceError] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [hostname, setHostname] = useState("");
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanHistoryCount, setScanHistoryCount] = useState(0);
  const [historyPagination, setHistoryPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [historyColumnFilters, setHistoryColumnFilters] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [findings, setFindings] = useState([]);
  const [findingCount, setFindingCount] = useState(0);
  const [findingError, setFindingError] = useState("");
  const [findingsLoading, setFindingsLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [findingColumnFilters, setFindingColumnFilters] = useState([]);
  const [promotionLoading, setPromotionLoading] = useState("");
  const historyRequestId = useRef(0);
  const findingsRequestId = useRef(0);
  const historyFilterValues = useMemo(
    () => Object.fromEntries(historyColumnFilters.map(({ id, value }) => [id, value])),
    [historyColumnFilters],
  );
  const findingFilterValues = useMemo(
    () => Object.fromEntries(findingColumnFilters.map(({ id, value }) => [id, value])),
    [findingColumnFilters],
  );

  useEffect(() => {
    if (!platformAdmin) return undefined;
    let cancelled = false;
    getTPRMCustomerAdminOptionsApi()
      .then((options) => {
        if (!cancelled) setTenantOptions(Array.isArray(options) ? options : []);
      })
      .catch((error) => {
        if (!cancelled) {
          setTenantError(getTPRMErrorMessage(error, "Unable to load tenant options."));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platformAdmin]);

  useEffect(() => {
    if (platformAdmin && !tenantId) {
      setVendors([]);
      return undefined;
    }
    let cancelled = false;
    setVendorError("");
    getTPRMVendorsApi({
      search: vendorQuery,
      limit: 50,
      offset: 0,
      ...(platformAdmin ? { tenant_id: tenantId } : {}),
    })
      .then((data) => {
        if (!cancelled) setVendors(normalizeTPRMPage(data).results);
      })
      .catch((error) => {
        if (!cancelled) setVendorError(getTPRMErrorMessage(error, "Unable to load vendors."));
      });
    return () => {
      cancelled = true;
    };
  }, [platformAdmin, tenantId, vendorQuery]);

  useEffect(() => {
    setSelectedScan(null);
    setScanHistory([]);
    setScanHistoryCount(0);
    setFindings([]);
    setFindingCount(0);
    setHistoryColumnFilters([]);
    setFindingColumnFilters([]);
    setHistoryPagination((current) => ({ ...current, pageIndex: 0 }));
    setPagination((current) => ({ ...current, pageIndex: 0 }));
    setVendorId("");
    setServiceId("");
    setServices([]);
  }, [tenantId]);

  useEffect(() => {
    if (!vendorId) {
      setServices([]);
      setServiceId("");
      return undefined;
    }
    let cancelled = false;
    setServiceError("");
    getTPRMVendorServicesApi({ vendor_id: vendorId, limit: 50, offset: 0 })
      .then((data) => {
        if (!cancelled) setServices(normalizeTPRMPage(data).results);
      })
      .catch((error) => {
        if (!cancelled) setServiceError(getTPRMErrorMessage(error, "Unable to load services."));
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  const loadHistory = useCallback(async () => {
    const requestId = ++historyRequestId.current;
    if (platformAdmin && !tenantId) {
      setScanHistory([]);
      setScanHistoryCount(0);
      setSelectedScan(null);
      setHistoryError("");
      setHistoryLoading(false);
      return;
    }
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const page = normalizeTPRMPage(
        await getTPRMDomainScansApi({
          ...historyFilterValues,
          limit: historyPagination.pageSize,
          offset: historyPagination.pageIndex * historyPagination.pageSize,
          ...(platformAdmin && tenantId ? { tenant_id: tenantId } : {}),
        }),
      );
      if (requestId !== historyRequestId.current) return;
      setScanHistory(page.results);
      setScanHistoryCount(page.count);
      if (!selectedScan && page.results.length) setSelectedScan(page.results[0]);
    } catch (error) {
      if (requestId === historyRequestId.current) {
        setHistoryError(getTPRMErrorMessage(error, "Unable to load domain scan history."));
      }
    } finally {
      if (requestId === historyRequestId.current) setHistoryLoading(false);
    }
  }, [
    historyFilterValues,
    historyPagination.pageIndex,
    historyPagination.pageSize,
    platformAdmin,
    selectedScan,
    tenantId,
  ]);

  const loadFindings = useCallback(async () => {
    const requestId = ++findingsRequestId.current;
    if (platformAdmin && !tenantId) {
      setFindings([]);
      setFindingCount(0);
      setFindingError("");
      setFindingsLoading(false);
      return;
    }
    setFindingsLoading(true);
    setFindingError("");
    try {
      const page = normalizeTPRMPage(
        await getTPRMDomainFindingsApi({
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          ...findingFilterValues,
          ...(platformAdmin ? { tenant_id: tenantId } : {}),
        }),
      );
      if (requestId !== findingsRequestId.current) return;
      setFindings(page.results);
      setFindingCount(page.count);
    } catch (error) {
      if (requestId === findingsRequestId.current) {
        setFindingError(getTPRMErrorMessage(error, "Unable to load domain findings."));
      }
    } finally {
      if (requestId === findingsRequestId.current) setFindingsLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    findingFilterValues,
    platformAdmin,
    tenantId,
  ]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  const selectedVendor = vendors.find((vendor) => vendor.vendor_id === vendorId);
  const openRun = useCallback(async (scanId) => {
    setHistoryError("");
    try {
      setSelectedScan(
        await getTPRMDomainScanApi(
          scanId,
          platformAdmin ? tenantId : undefined,
        ),
      );
    } catch (error) {
      setHistoryError(getTPRMErrorMessage(error, "Unable to load scan details."));
    }
  }, [platformAdmin, tenantId]);

  const scanHistoryColumns = useMemo(() => [
    {
      accessorKey: "scan_id",
      header: "Scan ID",
      meta: {
        filter: {
          variant: "text",
          label: "Scan ID",
          placeholder: "Filter scan ID",
        },
      },
    },
    {
      id: "vendor_id",
      accessorFn: (scan) => scan.vendor_id,
      header: "Vendor",
      cell: ({ row }) => `${displayTPRMValue(row.original.vendor_name)} (${displayTPRMValue(row.original.vendor_id)})`,
      meta: {
        filter: {
          variant: "text",
          label: "Vendor ID",
          placeholder: "Enter exact vendor ID",
        },
      },
    },
    {
      accessorKey: "target_hostname",
      header: "Hostname",
      meta: {
        filter: {
          variant: "text",
          label: "Hostname",
          placeholder: "Filter hostname",
        },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: {
        filter: {
          variant: "select",
          label: "Scan status",
          options: ["Completed", "Partial", "Failed", "Blocked", "Running"].map((value) => ({
            value,
            label: value,
          })),
        },
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => formatTPRMTimestamp(getValue()),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          aria-label={`View scan ${row.original.scan_id} for ${row.original.target_hostname}`}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          onClick={() => openRun(row.original.scan_id)}
          type="button"
        >
          View details
        </button>
      ),
    },
  ], [openRun]);

  const columns = useMemo(() => [
    {
      id: "vendor_id",
      accessorFn: (finding) => finding.vendor_id,
      header: "Vendor",
      cell: ({ row }) => `${displayTPRMValue(row.original.vendor_name)} (${displayTPRMValue(row.original.vendor_id)})`,
      meta: {
        filter: {
          variant: "text",
          label: "Vendor ID",
          placeholder: "Enter exact vendor ID",
        },
      },
    },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ getValue }) => displayTPRMValue(getValue()),
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          options: ["Critical", "High", "Medium", "Low"].map((value) => ({
            value,
            label: value,
          })),
        },
      },
    },
    {
      accessorKey: "title",
      header: "Finding",
      cell: ({ getValue }) => displayTPRMValue(getValue()),
    },
    {
      accessorKey: "target_hostname",
      header: "Affected hostname",
      cell: ({ getValue }) => displayTPRMValue(getValue()),
      meta: {
        filter: {
          variant: "text",
          label: "Hostname",
          placeholder: "Filter hostname",
        },
      },
    },
    {
      id: "service_id",
      accessorFn: (finding) => finding.service_id,
      header: "Service",
      cell: ({ row }) => row.original.service_name
        ? `${row.original.service_name} (${displayTPRMValue(row.original.service_id)})`
        : "—",
      meta: {
        filter: {
          variant: "text",
          label: "Service ID",
          placeholder: "Enter exact service ID",
        },
      },
    },
    {
      accessorKey: "evidence",
      header: "Source evidence",
      cell: ({ getValue }) => displayTPRMValue(JSON.stringify(getValue())),
    },
    {
      accessorKey: "last_seen_at",
      header: "Last observed",
      cell: ({ getValue }) => formatTPRMTimestamp(getValue()),
    },
    {
      accessorKey: "observation_count",
      header: "Observations",
      cell: ({ getValue }) => displayTPRMValue(getValue()),
    },
    {
      accessorKey: "remediation_guidance",
      header: "Remediation guidance",
      cell: ({ getValue }) => displayTPRMValue(getValue()),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const finding = row.original;
        if (finding.risk_finding_id) {
          return (
            <button
              aria-label={`Open Risk Register finding ${finding.risk_finding_id}`}
              className="rounded border border-blue-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300"
              onClick={() => navigate(
                `/ai-tprm?tab=risk-register&finding_id=${encodeURIComponent(finding.risk_finding_id)}`,
              )}
              type="button"
            >
              View Risk Register
            </button>
          );
        }
        if (!canStartScan) return <span>Not promoted</span>;
        return (
          <button
            aria-label={`Promote ${finding.title} to Risk Register`}
            className="rounded border border-blue-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-900 dark:text-blue-300"
            disabled={promotionLoading === finding.finding_id}
            onClick={async () => {
              setPromotionLoading(finding.finding_id);
              setFindingError("");
              try {
                const result = await promoteTPRMDomainFindingApi(
                  finding.finding_id,
                  platformAdmin ? tenantId : undefined,
                );
                setFindings((current) => current.map((item) => (
                  item.finding_id === finding.finding_id
                    ? { ...item, risk_finding_id: result.finding.finding_id }
                    : item
                )));
              } catch (error) {
                setFindingError(getTPRMErrorMessage(error, "Unable to promote this finding."));
              } finally {
                setPromotionLoading("");
              }
            }}
            type="button"
          >
            {promotionLoading === finding.finding_id ? "Promoting..." : "Promote to Risk Register"}
          </button>
        );
      },
    },
  ], [canStartScan, navigate, promotionLoading]);

  const startScan = async (event) => {
    event.preventDefault();
    setScanLoading(true);
    setScanError("");
    setSelectedScan(null);
    try {
      const result = await createTPRMDomainScanApi({
        ...(platformAdmin ? { tenant_id: tenantId } : {}),
        vendor_id: vendorId,
        ...(serviceId ? { service_id: serviceId } : {}),
        hostname,
        authorization_confirmed: authorizationConfirmed,
      });
      setSelectedScan(result);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
      await Promise.all([loadHistory(), loadFindings()]);
    } catch (error) {
      const scanId = error.response?.data?.scan_id;
      if (scanId) {
        try {
          setSelectedScan(await getTPRMDomainScanApi(
            scanId,
            platformAdmin ? tenantId : undefined,
          ));
        } catch (detailError) {
          setHistoryError(getTPRMErrorMessage(detailError, "Unable to load failed scan details."));
        }
      }
      setScanError(getTPRMErrorMessage(error, "Unable to start the domain posture scan."));
      await loadHistory();
    } finally {
      setScanLoading(false);
    }
  };

  return (
    <section
      aria-labelledby="tprm-domain-posture-heading"
      className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5"
    >
      <header>
        <h2
          className="text-lg font-semibold text-slate-900 dark:text-white"
          id="tprm-domain-posture-heading"
        >
          Domain security posture
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-slate-600 dark:text-slate-400">
          Authorized read-only observations only: public DNS resolution, HTTPS/TLS validation,
          same-host redirects, and six common HTTP security headers. This is not a penetration
          test and a completed check does not prove a domain is vulnerability-free.
        </p>
      </header>

      {canStartScan ? (
        <form className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-950/50 md:grid-cols-2" onSubmit={startScan}>
          {platformAdmin && (
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Customer tenant
              <select
                aria-label="Customer tenant"
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 dark:border-slate-700 dark:bg-slate-900"
                onChange={(event) => {
                  setAuthorizationConfirmed(false);
                  setTenantId(event.target.value);
                }}
                required
                value={tenantId}
              >
                <option value="">Select a tenant</option>
                {tenantOptions.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}{tenant.company_name ? ` — ${tenant.company_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Find vendor
            <input
              aria-label="Search vendors"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 dark:border-slate-700 dark:bg-slate-900"
              onChange={(event) => setVendorQuery(event.target.value)}
              placeholder="Search vendor name"
              value={vendorQuery}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Vendor
            <select
              aria-label="Vendor"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 dark:border-slate-700 dark:bg-slate-900"
              disabled={platformAdmin && !tenantId}
              onChange={(event) => {
                setAuthorizationConfirmed(false);
                setVendorId(event.target.value);
              }}
              required
              value={vendorId}
            >
              <option value="">Select a vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.vendor_id} value={vendor.vendor_id}>
                  {vendor.name} ({vendor.vendor_id})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Service (optional)
            <select
              aria-label="Service"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 dark:border-slate-700 dark:bg-slate-900"
              disabled={!vendorId}
              onChange={(event) => {
                setAuthorizationConfirmed(false);
                setServiceId(event.target.value);
              }}
              value={serviceId}
            >
              <option value="">Vendor domain (no service)</option>
              {services.map((service) => (
                <option key={service.service_id} value={service.service_id}>
                  {service.service_name} ({service.service_id})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200 md:col-span-2">
            Authorized public hostname
            <input
              aria-label="Authorized public hostname"
              autoComplete="off"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 dark:border-slate-700 dark:bg-slate-900"
              onChange={(event) => {
                setAuthorizationConfirmed(false);
                setHostname(event.target.value);
              }}
              placeholder="example.com"
              required
              value={hostname}
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 md:col-span-2">
            <input
              aria-label="Confirm authorization to assess hostname"
              checked={authorizationConfirmed}
              className="mt-1"
              onChange={(event) => setAuthorizationConfirmed(event.target.checked)}
              required
              type="checkbox"
            />
            <span>
              I confirm that {selectedVendor?.name || "the selected vendor"} is authorized to
              assess this hostname for the limited read-only checks shown above.
            </span>
          </label>
          {(tenantError || vendorError || serviceError) && (
            <p className="text-sm text-rose-700 dark:text-rose-300 md:col-span-2" role="alert">
              {[tenantError, vendorError, serviceError].filter(Boolean).join(" ")}
            </p>
          )}
          <div className="md:col-span-2">
            <button
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={scanLoading || !vendorId || !hostname.trim() || !authorizationConfirmed || (platformAdmin && !tenantId)}
              type="submit"
            >
              {scanLoading ? "Running bounded checks..." : "Start authorized scan"}
            </button>
          </div>
        </form>
      ) : (
        <p className="rounded-lg border border-slate-200 p-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Domain scans can only be started by a customer or platform administrator. You can view scan history and findings available to your role.
        </p>
      )}

      {scanError && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200" role="alert">
          {scanError}
        </p>
      )}

      {selectedScan && (
        <section aria-label="Selected domain scan details" className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-900 dark:text-white">
              Scan {selectedScan.scan_id} — {selectedScan.target_hostname}
            </h3>
            <span className={`font-semibold ${STATUS_STYLE[selectedScan.status] || ""}`}>
              {selectedScan.status}
            </span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Started {formatTPRMTimestamp(selectedScan.started_at)} · Completed {formatTPRMTimestamp(selectedScan.completed_at)} · Scanner {selectedScan.scanner_version}
          </p>
          {selectedScan.error_summary && (
            <p className="text-sm text-amber-800 dark:text-amber-300" role="status">
              {selectedScan.error_summary}
            </p>
          )}
          <ul className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {(selectedScan.checks || []).map((check) => (
              <li className="rounded bg-slate-50 p-3 dark:bg-slate-950/60" key={check.check_key}>
                <span className="font-medium">{check.check_key}: {check.status}</span>
                <p className="mt-1 text-slate-600 dark:text-slate-400">{check.summary}</p>
              </li>
            ))}
          </ul>
          {(selectedScan.findings || []).length > 0 ? (
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Findings by severity: {["Critical", "High", "Medium", "Low"].map((severity) => {
                const count = selectedScan.findings.filter((finding) => finding.severity === severity).length;
                return `${severity} ${count}`;
              }).join(" · ")}
            </p>
          ) : selectedScan.status === "Completed" ? (
            <p className="text-sm text-slate-600 dark:text-slate-400" role="status">
              No findings were recorded by the supported checks; this does not establish that the hostname is vulnerability-free.
            </p>
          ) : null}
        </section>
      )}

      <section aria-labelledby="tprm-domain-scan-history-heading">
        <h3 className="font-semibold text-slate-900 dark:text-white" id="tprm-domain-scan-history-heading">
          Recent scan history
        </h3>
        {historyError && (
          <p className="mt-2 text-sm text-rose-700 dark:text-rose-300" role="alert">{historyError}</p>
        )}
        <DataTable
          columns={scanHistoryColumns}
          data={scanHistory}
          emptyMessage={
            platformAdmin && !tenantId
              ? "Select a tenant to view domain scan history."
              : "No domain scans have been recorded in this scope."
          }
          enableColumnFilters
          enableGlobalFilter={false}
          enableSorting={false}
          getRowId={(scan) => scan.scan_id}
          loading={historyLoading}
          manualFiltering
          manualPagination
          columnFilters={historyColumnFilters}
          onColumnFiltersChange={(updater) => {
            const next = typeof updater === "function" ? updater(historyColumnFilters) : updater;
            setHistoryColumnFilters(next);
            setHistoryPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          onPaginationChange={(updater) => setHistoryPagination((current) => (
            typeof updater === "function" ? updater(current) : updater
          ))}
          pageCount={Math.ceil(scanHistoryCount / historyPagination.pageSize)}
          pagination={historyPagination}
          rowCount={scanHistoryCount}
        />
      </section>

      <section aria-labelledby="tprm-domain-findings-heading">
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white" id="tprm-domain-findings-heading">
              Domain posture findings
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {vendorId ? `Showing findings for ${selectedVendor?.name || vendorId}.` : "Tenant-scoped observations from saved scans."}
              {" "}Clean later scans do not close existing Risk Register findings.
            </p>
          </div>
        </div>
        {findingError && (
          <p className="mb-2 text-sm text-rose-700 dark:text-rose-300" role="alert">{findingError}</p>
        )}
        <DataTable
          columns={columns}
          data={findings}
          emptyMessage={
            platformAdmin && !tenantId
              ? "Select a tenant to view domain posture findings."
              : "No domain posture findings are recorded."
          }
          enableColumnFilters
          enableGlobalFilter={false}
          enableSorting={false}
          getRowId={(finding) => finding.finding_id}
          loading={findingsLoading}
          manualFiltering
          manualPagination
          columnFilters={findingColumnFilters}
          onColumnFiltersChange={(updater) => {
            const next = typeof updater === "function" ? updater(findingColumnFilters) : updater;
            setFindingColumnFilters(next);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          onPaginationChange={(updater) => setPagination((current) => (
            typeof updater === "function" ? updater(current) : updater
          ))}
          pageCount={Math.ceil(findingCount / pagination.pageSize)}
          pagination={pagination}
          rowCount={findingCount}
        />
      </section>
    </section>
  );
};

export default DomainPosturePanel;
